import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { confetti, flyCoins, playCoin } from "@/lib/celebrate";

export const POINTS_EVENT = "points-awarded";
const COMBO_MS = 12000;

export function useCountUp(value, ms = 900, instantAbove = Infinity) {
  const [shown, setShown] = useState(value);
  const cur = useRef(value);
  useEffect(() => {
    const target = Number(value) || 0;
    const start = performance.now(), a = cur.current;
    if (a === target) return undefined;
    // big jumps (first load, account switch) are shown instantly instead of counting up from scratch
    if (Math.abs(target - a) > instantAbove) { cur.current = target; setShown(target); return undefined; }
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - start) / ms), e = 1 - Math.pow(1 - k, 3);
      cur.current = Math.round(a + (target - a) * e);
      setShown(cur.current);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms, instantAbove]);
  return shown;
}

function BurstCard({ burst }) {
  const amount = useCountUp(burst.amount, 700);
  const big = burst.combo >= 3 || burst.amount >= 20;
  return (
    <div key={burst.key} className={`points-burst relative flex flex-col items-center gap-1 px-8 py-5 rounded-[2rem] bg-gradient-to-br from-amber-300 via-orange-400 to-pink-500 text-white ${big ? "burst-mega" : "shadow-[0_0_60px_rgba(244,114,182,0.7)]"}`} data-testid="points-burst">
      <span className="burst-rays" aria-hidden />
      {burst.combo >= 2 && <span className="combo-chip absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-900 text-amber-300 text-xs font-extrabold whitespace-nowrap shadow-lg" data-testid="points-combo"><Flame className="w-3.5 h-3.5 fill-orange-500 text-orange-400" /> Combo ×{burst.combo}</span>}
      <span className="relative flex items-center gap-2 font-display text-4xl font-extrabold drop-shadow" data-testid="points-burst-amount"><Coins className="w-8 h-8 coin-spin" /> +{amount}</span>
      <span className="relative text-sm font-bold text-white/95 max-w-[260px] text-center" data-testid="points-burst-reason">{burst.reason}</span>
    </div>
  );
}

export function PointsBurst() {
  const { refreshUser } = useAuth();
  const [burst, setBurst] = useState(null);
  const combo = useRef({ n: 0, at: 0 });
  useEffect(() => {
    let t;
    const h = (e) => {
      const nowMs = Date.now();
      combo.current = { n: nowMs - combo.current.at < COMBO_MS ? combo.current.n + 1 : 1, at: nowMs };
      const n = combo.current.n;
      setBurst({ ...e.detail, combo: n, key: nowMs });
      playCoin(n);
      confetti({ count: Math.min(30 + n * 12, 110), spread: 1 + Math.min(n, 5) * 0.15 });
      flyCoins({ count: Math.min(4 + Math.ceil(e.detail.amount / 3), 14) });
      setTimeout(refreshUser, 900);
      window.dispatchEvent(new Event("points-changed"));
      clearTimeout(t);
      t = setTimeout(() => setBurst(null), 2300);
    };
    window.addEventListener(POINTS_EVENT, h);
    return () => { window.removeEventListener(POINTS_EVENT, h); clearTimeout(t); };
  }, [refreshUser]);
  if (!burst) return null;
  return (
    <div className="fixed inset-0 z-[90] pointer-events-none flex items-center justify-center" aria-live="polite">
      <BurstCard key={burst.key} burst={burst} />
    </div>
  );
}

export function PointsBadge({ points, testId, className = "" }) {
  const [flash, setFlash] = useState(null);
  const shown = useCountUp(points, 1100, 2000);
  useEffect(() => {
    let t;
    const h = (e) => { setFlash({ amount: e.detail.amount, key: Date.now() }); clearTimeout(t); t = setTimeout(() => setFlash(null), 1800); };
    window.addEventListener(POINTS_EVENT, h);
    return () => { window.removeEventListener(POINTS_EVENT, h); clearTimeout(t); };
  }, []);
  return (
    <Link to="/dashboard" data-testid={testId} title="Dein Kontostand – diese Punkte kannst du gegen Geschenke eintauschen" aria-label={`Kontostand: ${points} Punkte`} className={`relative flex items-center gap-1.5 rounded-full border font-bold text-sm transition-colors duration-300 ${flash ? "points-flash bg-amber-300 border-amber-400 text-amber-950" : "bg-amber-50 border-amber-200 text-amber-700"} ${className}`}>
      <Coins className={`w-4 h-4 ${flash ? "coin-spin" : ""}`} /> <span className="tabular-nums" data-testid={`${testId}-value`}>{shown}</span>
      {flash && <span key={flash.key} className="points-float absolute -top-2 -right-2 px-1.5 rounded-full bg-pink-500 text-white text-[11px] font-extrabold" data-testid={`${testId}-flash`}>+{flash.amount}</span>}
    </Link>
  );
}
